import { Connection, Proxy } from 'signalr-no-jquery';
import { SendMessageCommandParameters } from '../types';

class SignalRService {

    private connection: any;
    private hubProxy: any;
    private setOnConnected: any;
    private roomId: string = '';
    private lifecycleWired: boolean = false;
    private startPromise: Promise<void> | null = null;
    private joinedRoomId: string = '';

    constructor(signalRConnection: Connection, hubProxy: Proxy, setOnConnected?: React.Dispatch<React.SetStateAction<boolean>>) {
        this.connection = signalRConnection
        this.hubProxy = hubProxy
        this.setOnConnected = setOnConnected
    }

    startConnection = async (roomId: string) => {
        this.roomId = roomId;
        this.wireLifecycleEvents();

        try {
            await this.ensureStarted();
            await this.joinChatRoom(roomId);
        } catch (err) {
            console.error('Error starting SignalR connection:', err);
        }
    };

    // Wire connection lifecycle events so `connected` state reflects
    // disconnect / reconnect in addition to the initial connect.
    private wireLifecycleEvents = () => {
        if (this.lifecycleWired) return;
        this.lifecycleWired = true;

        if (typeof this.connection.disconnected === 'function') {
            this.connection.disconnected(() => {
                console.log('SignalR disconnected');
                this.startPromise = null;
                this.joinedRoomId = '';
                this.setOnConnected?.(false);
            });
        }

        if (typeof this.connection.reconnecting === 'function') {
            this.connection.reconnecting(() => {
                console.log('SignalR reconnecting');
                this.setOnConnected?.(false);
            });
        }

        if (typeof this.connection.reconnected === 'function') {
            this.connection.reconnected(async () => {
                console.log('SignalR reconnected, re-joining room', this.roomId);
                // Re-join the room so server-side group membership is restored,
                // then mark connected so the pending queue flushes.
                this.joinedRoomId = '';
                try {
                    await this.joinChatRoom(this.roomId);
                } catch (error) {
                    console.log("Error while re-joining after reconnect", error);
                }
            });
        }
    };

    private ensureStarted = async () => {
        if (!this.startPromise) {
            this.startPromise = new Promise<void>((resolve, reject) => {
                this.connection.start()
                    .done(() => {
                        resolve();
                    })
                    .fail((error: any) => {
                        this.startPromise = null;
                        reject(error);
                    });
            });
        }

        return this.startPromise;
    };

    joinChatRoom = async (roomId: string) => {
        if (!roomId) {
            return;
        }

        if (this.joinedRoomId === roomId) {
            this.setOnConnected?.(true);
            return;
        }

        try {
            await this.hubProxy.invoke('joinChatRoom', roomId);
            this.joinedRoomId = roomId;
            this.setOnConnected?.(true);
            console.log("Joined successfully.");
        } catch (error) {
            this.setOnConnected?.(false);
            this.joinedRoomId = '';
            console.log("Error while joining", error);
            throw error;
        }
    };

    sendMessage = async (message: SendMessageCommandParameters) => {
        try {
            await this.ensureStarted();

            if (message.ChatRoomId) {
                await this.joinChatRoom(message.ChatRoomId);
            }

            await this.hubProxy.invoke('Send', message);
        } catch (error) {
            console.log("Error while sending the message", error);
            throw error;
        }
    }
};

export default SignalRService;
